// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, ExclamationCircleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { URL } from '../service/Service';

const request = async (path, options = {}) => {
  const token = sessionStorage.getItem('jwt');
  const response = await fetch(`${URL}${path}`, {
    ...options,
    headers: {
      Accept: 'application/json',
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!response.ok) throw new Error((await response.text()) || 'Não foi possível concluir a operação.');
  return response.status === 204 ? null : response.json();
};

export default function TwoFactorSettings() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    request('/api/users/2fa')
      .then((data) => setEnabled(Boolean(data.enabled)))
      .catch((error) => setFeedback({ type: 'error', message: error.message }))
      .finally(() => setLoading(false));
  }, []);

  const startSetup = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      setSetup(await request('/api/users/2fa/setup', {
        method: 'POST',
        body: JSON.stringify({ currentPassword: password }),
      }));
      setPassword('');
      setCode('');
      setRecoveryCodes([]);
    } catch (error) {
      setFeedback({ type: 'error', message: error.message });
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async (event) => {
    event.preventDefault();
    setBusy(true);
    setFeedback(null);
    try {
      const data = await request('/api/users/2fa/confirm', {
        method: 'POST',
        body: JSON.stringify({ code }),
      });
      setEnabled(true);
      setSetup(null);
      setCode('');
      setRecoveryCodes(data.recoveryCodes || []);
      setFeedback({ type: 'success', message: 'Autenticação de dois fatores ativada.' });
    } catch (error) {
      setFeedback({ type: 'error', message: error.message });
    } finally {
      setBusy(false);
    }
  };

  const disable = async (event) => {
    event.preventDefault();
    setBusy(true);
    setFeedback(null);
    try {
      await request('/api/users/2fa', {
        method: 'DELETE',
        body: JSON.stringify({ code, currentPassword: password }),
      });
      setEnabled(false);
      setCode('');
      setPassword('');
      setRecoveryCodes([]);
      setFeedback({ type: 'success', message: 'Autenticação de dois fatores desativada.' });
    } catch (error) {
      setFeedback({ type: 'error', message: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-6 sm:px-8">
        <div className="flex items-start gap-4">
          <span className="rounded-2xl bg-emerald-50 p-3 text-emerald-700"><ShieldCheckIcon className="h-6 w-6" /></span>
          <div>
            <h2 className="text-2xl font-bold text-slate-950">Autenticação de dois fatores</h2>
            <p className="mt-1 text-sm text-slate-600">Proteja a conta com um código da sua aplicação Authenticator.</p>
          </div>
        </div>
        {!loading && <span className={`rounded-full px-3 py-1 text-xs font-semibold ${enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}`}>{enabled ? 'Ativa' : 'Inativa'}</span>}
      </div>

      <div className="space-y-5 p-6 sm:p-8">
        {loading ? <p className="text-sm text-slate-500">A carregar estado de segurança...</p> : (
          <>
            {!enabled && !setup && (
              <div className="max-w-md space-y-4">
                <div><label htmlFor="two-factor-setup-password" className="ui-label">Confirme a palavra-passe atual</label><input id="two-factor-setup-password" type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} className="ui-input mt-2" /></div>
                <button type="button" disabled={busy || !password} onClick={startSetup} className="ui-button-primary">{busy ? 'A preparar...' : 'Ativar 2FA'}</button>
              </div>
            )}

            {setup && (
              <form onSubmit={confirmSetup} className="grid items-start gap-6 md:grid-cols-[200px_1fr]">
                <img src={setup.qrCodeDataUrl} alt="QR Code para configurar a autenticação de dois fatores" className="h-[200px] w-[200px] rounded-2xl border border-slate-200" />
                <div className="space-y-4">
                  <div><p className="text-sm font-semibold text-slate-800">1. Leia o QR Code na aplicação Authenticator.</p><p className="mt-1 break-all rounded-xl bg-slate-50 p-3 font-mono text-xs text-slate-600">{setup.secret}</p></div>
                  <div><label htmlFor="two-factor-confirm-code" className="ui-label">2. Introduza o código de 6 dígitos</label><input id="two-factor-confirm-code" className="ui-input mt-2 max-w-xs text-center text-lg font-bold tracking-[0.25em]" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} placeholder="000000" /></div>
                  <button type="submit" disabled={busy || code.length !== 6} className="ui-button-primary">{busy ? 'A confirmar...' : 'Confirmar e ativar'}</button>
                </div>
              </form>
            )}

            {enabled && recoveryCodes.length === 0 && (
              <form onSubmit={disable} className="grid gap-4 md:grid-cols-2">
                <div><label htmlFor="two-factor-disable-password" className="ui-label">Palavra-passe atual</label><input id="two-factor-disable-password" type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} className="ui-input mt-2" /></div>
                <div><label htmlFor="two-factor-disable-code" className="ui-label">Código da aplicação</label><input id="two-factor-disable-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} className="ui-input mt-2 text-center font-bold tracking-[0.25em]" /></div>
                <div className="md:col-span-2"><button type="submit" disabled={busy || !password || code.length !== 6} className="ui-button-secondary">{busy ? 'A desativar...' : 'Desativar 2FA'}</button></div>
              </form>
            )}

            {recoveryCodes.length > 0 && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                <h3 className="font-bold text-amber-950">Guarde estes códigos de recuperação</h3>
                <p className="mt-1 text-sm text-amber-800">Cada código só pode ser utilizado uma vez. Não voltarão a ser apresentados.</p>
                <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">{recoveryCodes.map((item) => <code key={item} className="rounded-lg bg-white px-3 py-2 text-center font-bold text-slate-800">{item}</code>)}</div>
                <button type="button" className="ui-button-secondary mt-4" onClick={() => navigator.clipboard?.writeText(recoveryCodes.join('\n'))}>Copiar códigos</button>
              </div>
            )}
          </>
        )}

        {feedback && <div className={`flex items-start gap-3 rounded-2xl border p-4 text-sm ${feedback.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-800'}`}>{feedback.type === 'success' ? <CheckCircleIcon className="h-5 w-5" /> : <ExclamationCircleIcon className="h-5 w-5" />}<p className="font-medium">{feedback.message}</p></div>}
      </div>
    </section>
  );
}
